import React, { useCallback, useEffect, useState } from 'react';
import { errorText, formatDate } from './strengthFormat';

/**
 * Free-text wishes logged outside of any workout, e.g. "I'd like more defined arms and
 * shoulders". Kept as history, private to the owner, and read by the AI coach the next
 * time it writes a plan (see StrengthPlanGenerator.RecentGoalsAsync on the backend).
 */
function CoachNotes({ api, t, lang, showMessage }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setNotes(await api.coachingNotes() || []);
    } catch (e) {
      setNotes([]);
      showMessage?.(errorText(e, t('strength.errors.load')), 'error');
    }
  }, [api, showMessage, t]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await api.addCoachingNote(trimmed);
      setText('');
      showMessage?.(t('strength.goals.added'));
      await load();
    } catch (err) {
      showMessage?.(errorText(err, t('strength.errors.save')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (note) => {
    if (!window.confirm(t('strength.goals.deleteConfirm'))) return;
    try {
      await api.deleteCoachingNote(note.id);
      setNotes(prev => prev.filter(n => n.id !== note.id));
    } catch (err) {
      showMessage?.(errorText(err, t('strength.errors.save')), 'error');
    }
  };

  return (
    <div className="st-coach-notes">
      <h4>💬 {t('strength.goals.title')}</h4>
      <p className="st-muted st-small">{t('strength.goals.hint')}</p>
      <form className="st-row" onSubmit={add}>
        <input
          className="st-input"
          type="text"
          value={text}
          maxLength={500}
          placeholder={t('strength.goals.placeholder')}
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" className="st-btn st-btn-primary" disabled={saving || !text.trim()}>
          {t('strength.goals.add')}
        </button>
      </form>

      {notes === null ? null : notes.length === 0 ? (
        <p className="st-muted st-small">{t('strength.goals.empty')}</p>
      ) : (
        <ul className="st-coach-note-list">
          {notes.map(note => (
            <li key={note.id}>
              <span>{note.text}</span>
              <span className="st-row">
                <span className="st-muted st-small">{formatDate(new Date(note.createdAt), lang)}</span>
                <button type="button" className="st-link-btn" onClick={() => remove(note)}>{t('strength.goals.delete')}</button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="st-muted st-small">🔒 {t('strength.goals.privacy')}</p>
    </div>
  );
}

export default CoachNotes;
